import React, { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import XIcon from 'public/assets/icons/x.svg';
import THDialogWrapper from '@/components/general/THDialogWrapper';
import Button from '@/components/general/Button';
import useCart from '@/hooks/useCart';
import CartRow from '@/components/Layout/Header/Cart/CartRow';
import Price from '@/components/general/Price';
import CartAlsoLikeRow from '@/components/Layout/Header/Cart/CartAlsoLikeRow';
import EmptyState from '@/components/general/EmptyState';
import { useQuery } from '@tanstack/react-query';
import { getCartMayAlsoLikeQueryFn } from '@/queries/cart';
import If, { Else } from '@/components/util/IF';
import i18next from 'i18next';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';

type ProductOfferProps = {
    onHide: () => void;
    show: boolean;
};

const CartModal = (props: ProductOfferProps) => {
    const { onHide, show } = props;
    const { cart } = useCart();

    const pathname = usePathname();

    useEffect(() => {
        if (show) {
            onHide();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const { data: mayAlsoLike } = useQuery({
        queryKey: ['getCartMayAlsoLikeQueryFn'],
        queryFn: getCartMayAlsoLikeQueryFn,
        enabled: !!cart?.items && cart?.items.length > 0,
    });

    return (
        <Transition.Root show={show} as={Fragment}>
            <THDialogWrapper
                as="div"
                className="relative z-[200]"
                onClose={() => {
                    onHide();
                }}>
                <motion.div
                    style={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onHide}
                    className="fixed inset-0 backdrop-blur-sm bg-black/20 transition-opacity z-[100]" />
                <div className="fixed z-[100] overflow-y-auto right-[0px] lg:right-[30px] lg:top-[0px] top-[0px] bottom-[0px] lg:bottom-[0px] left-[0px] lg:left-[auto]">
                    <div className="flex min-h-full items-end justify-end p-4 sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform rounded-[8px] bg-white shadow-xl transition-all w-full lg:w-[30vw] lg:h-[94vh] h-[100vh] flex flex-col justify-between overflow-y-hidden">
                                <button
                                    className="cursor-pointer absolute right-[15px] top-[30px]"
                                    onClick={onHide}><XIcon className="scale-[0.8]" />
                                </button>
                                <div className="">
                                    <div className="text-left border-b-[1px] border-grey13 p-[24px]">
                                        <h3 className="text-[20px] font-[500]">{i18next.t('cart.your-bag', { count: cart?.items.reduce((sum, item) => sum + item.quantity, 0) ?? 0 })}</h3>
                                    </div>
                                    <div className="p-[28px] flex-1 overflow-y-auto" style={{ height: 'calc(86.4vh - 139px)' }}>
                                        <If condition={cart && cart.items && cart.items.length > 0}>
                                            <div className="flex flex-col gap-[24px]">
                                                {cart?.items.map((item) => {
                                                    return <CartRow key={`${item.id}-${item?.quantity ?? 0}`} cartItem={item} />;
                                                })}
                                            </div>
                                            <If condition={mayAlsoLike && mayAlsoLike.current_auction}>
                                                <div className="">
                                                    <div className="h-[1px] w-full bg-[#EFF0F0] my-[32px]" />
                                                    <h3 className="text-[20px] font-[500] mb-[28px]">{i18next.t('cart.you-may-also-like')}</h3>
                                                    {mayAlsoLike && <CartAlsoLikeRow cartItem={mayAlsoLike} />}
                                                </div>
                                            </If>
                                            <Else>
                                                <EmptyState
                                                    actionText={i18next.t('general.marketplace')}
                                                    pathRoute="/marketplace"
                                                    onLinkClick={() => onHide()}
                                                    title={i18next.t('cart.no-items-in-bag-title')}
                                                    subtitle={i18next.t('cart.no-items-in-bag-description')} />
                                            </Else>
                                        </If>
                                    </div>
                                </div>
                                <If condition={cart?.items && cart?.items?.length > 0}>
                                    <div className="flex flex-col justify-between p-[24px] gap-[16px]">

                                        <div className="flex flex-row justify-between text-[20px] font-[500]">
                                            <span>{i18next.t('cart.subtotal')}</span>
                                            <Price number={cart?.items_total_amount} precision={2} />
                                        </div>
                                        <Button className="w-full" href="/checkout" onClick={onHide}>{i18next.t('cart.continue-to-checkout')}</Button>
                                    </div>
                                </If>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </THDialogWrapper>
        </Transition.Root>
    );
};

export default CartModal;
